<template>
  <v-container class="container">
    <v-form ref="deliveryClientForm" v-model="deliveryClientFormValid" @submit.prevent="submit">
      <v-row class="custom-row-overflow">
        <v-col cols="12">
          <p class="title">{{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.TITLE') }}</p>
        </v-col>

        <v-col cols="12">
          <p class="sub-title mb-1">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.SUBTITLE') }}
          </p>
          <p class="text-condition">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.FIRST_CONDITION') }}
          </p>
          <p class="text-condition">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.SECOND_CONDITION') }}
          </p>
          <p class="text-condition">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.THIRD_CONDITION') }}
          </p>
          <p class="text-condition">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.FOURS_CONDITION') }}
          </p>
        </v-col>

        <!--END_STATUS-->
        <v-col cols="12">
          <p required="true" class="text-field">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.END_STATUS') }}
          </p>
          <v-autocomplete
            id="select-end-status"
            v-model="selectedEndStatus"
            :items="endStatusList"
            :no-data-text="$t('COMMON.NO_DATA')"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.END_STATUS')"
            hide-details
            item-title="name"
            item-value="technical_id"
            required
            :rules="requiredRules"
            :disabled="!currentRepairFile.product_boomerang_id"
          >
            <template v-slot:selection="{ item }">
              <v-chip size="small">
                <span>{{ item.technical_id }}</span>
                <span>{{ item.title }}</span>
              </v-chip>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item :title="item.title" :value="item.value" v-bind="props">
                <v-tooltip
                  v-if="shouldShowTooltip(item, 'select-end-status')"
                  activator="parent"
                  location="bottom left"
                >
                  {{ item.title }}
                </v-tooltip>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
        <!--PRODUCT BOOMERANG ID-->
        <v-col cols="12" v-if="deliveryClient && !showReplacementProduct">
          <p class="text-field">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.PRODUCT_BOOMERANG_NAME') }}
          </p>
          <v-text-field
            v-model="productBoomerangName"
            disabled
            :error-messages="error?.productBoomerangName"
            :hide-details="!error?.productBoomerangName"
          />
        </v-col>
        <!--SELECT PRODUCT BOOMERANG ID-->
        <v-col cols="12" v-if="deliveryClient && showReplacementProduct">
          <p required="true" class="text-field">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.SELECT_PRODUCT') }}
          </p>
          <v-autocomplete
            id="select-product"
            v-model="selectedProduct"
            :items="productList"
            :no-data-text="$t('COMMON.NO_DATA')"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.PRODUCT')"
            hide-details
            item-title="boomerangName"
            item-value="technicalId"
            :disabled="!currentRepairFile.product_boomerang_id"
          >
            <template v-slot:selection="{ item }">
              <v-chip size="small">
                <span>{{ item.title }}</span>
              </v-chip>
            </template>
            <template v-slot:item="{ item, props }">
              <v-list-item :title="item.title" :value="item.value" v-bind="props">
                <v-tooltip
                  v-if="shouldShowTooltip(item, 'select-product')"
                  activator="parent"
                  location="bottom left"
                >
                  {{ item.title }}
                </v-tooltip>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
        <!--SERIAL NUMBER-->
        <v-col cols="12" v-if="deliveryClient">
          <div class="d-flex justify-lg-space-between">
            <p required="true" class="text-field">
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.SERIAL_NUMBER') }}
            </p>
            <v-dialog
              v-if="product && product?.serialNumberPicture"
              v-model="dialogVisible"
              max-width="400"
            >
              <template v-slot:activator>
                <div @click="dialogVisible = true" @click.stop.prevent class="clickable">
                  <div v-if="product && product?.serialNumberPicture" class="d-flex">
                    <img
                      alt="check-icon"
                      class="file-svg mr-2"
                      src="@/assets/icons/file/file.svg"
                    />
                    <p class="serial-content">
                      {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.BUTTON.SERIAL_NUMBER') }}
                    </p>
                  </div>
                </div>
              </template>
              <v-card>
                <div class="dialog-picture">
                  <v-col class="pa-0" v-if="product && product.serialNumberPicture">
                    <v-img :src="product.serialNumberPicture" alt="Product Preview" />
                  </v-col>
                </div>
                <button class="dialog-close-btn" @click="dialogVisible = false">
                  <img alt="dialog-icon" class="dialog-img" src="@/assets/icons/file/cross.svg" />
                </button>
              </v-card>
            </v-dialog>
          </div>
          <v-text-field
            v-model="serialNumber"
            :placeholder="$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PLACEHOLDER.SEARCH')"
            :disabled="
              (!selectedProduct && showReplacementProduct) ||
              !currentRepairFile.product_boomerang_id
            "
            :rules="requiredRules"
            :error-messages="error?.serialNumber"
            :hide-details="!error?.serialNumber"
          >
            <template v-slot:message="{ message }">
              <img alt="dialog-icon" class="red-cross-svg" src="@/assets/icons/file/cross.svg" />
              {{ message }}
            </template>
          </v-text-field>
          <p class="generic-serial-number" v-if="isGenericSerialNumber">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.MESSAGE.GENERIC_SERIAL_NUMBER') }}
          </p>
        </v-col>
        <!--PRODUCT END COST-->
        <v-col cols="12" v-if="deliveryClient">
          <p required="true" class="text-field">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.PRODUCT_END_COST') }}
          </p>
          <div class="d-flex align-center">
            <v-text-field
              :disabled="!serialNumber || !currentRepairFile.product_boomerang_id"
              v-model="productEndCost"
              required
              type="number"
              :rules="[...isNumberRules, ...positiveNumberRules]"
              :error-messages="error?.productEndCost"
              :hide-details="!error?.productEndCost"
            />
            <p class="font-weight-bold price-content">
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.EURO') }}
            </p>
          </div>
        </v-col>

        <!--BILLING NUMBER-->
        <v-col cols="12">
          <p class="text-field">
            {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.BILLING_NUMBER') }}
          </p>
          <v-text-field
            v-model="billingNumber"
            maxlength="20"
            :error-messages="error?.billingNumber"
            :disabled="!currentRepairFile.product_boomerang_id"
            :hide-details="!error?.billingNumber"
          />
        </v-col>

        <!--BILLING DETAIL-->
        <v-col cols="12" class="pa-0 information-content">
          <v-col cols="12" class="margin-top-18 text-field pt-0 pb-0">
            <div class="d-flex align-center justify-space-between">
              <p class="font-weight-bold">
                {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.INITIAL_INFORMATION') }}
              </p>
            </div>
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="underline" />
          </v-col>

          <!--PACKAGE FRNS BILLING CODE-->
          <v-col cols="12" class="text-field pt-0 pb-0">
            <div class="d-flex align-center justify-space-between">
              <p>
                {{
                  $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.INTERNAL_PROJECT_INFORMATION')
                }}
              </p>
              <p class="font-weight-bold">{{ currentRepairFile.package_frns_billing_code }}</p>
            </div>
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="underline" />
          </v-col>

          <!--PACKAGE NETWORK CODE-->
          <v-col cols="12" class="text-field pt-0 pb-0">
            <div class="d-flex align-center justify-space-between">
              <p>
                {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REFERENCE') }}
              </p>
              <p class="font-weight-bold">{{ currentRepairFile.package_network_code }}</p>
            </div>
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="underline" />
          </v-col>

          <!--PACKAGE DESIGNATION-->
          <v-col cols="12" class="text-field pt-0 pb-0">
            <div class="d-flex align-center justify-space-between">
              <p>
                {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.PACKAGE_DESIGNATION') }}
              </p>
              <p class="font-weight-bold">{{ currentRepairFile.package_designation }}</p>
            </div>
          </v-col>
          <v-col class="py-0" cols="12">
            <div class="underline" />
          </v-col>
        </v-col>

        <v-col cols="12">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between">
                <p class="font-weight-bold">
                  {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.BILLING_CLIENT_COST') }}
                </p>
              </div>
            </v-col>

            <!--REPAIR/CHANGE PRODUCT COST-->
            <v-col cols="12">
              <div class="d-flex justify-space-between align-center">
                <p class="font-weight-bold mb-0">
                  {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REPAIR_CHANGE_COST') }}
                </p>
                <div class="d-flex align-center">
                  <v-text-field
                    class="number-text-field"
                    v-model="packageClientPrice"
                    type="number"
                    :error-messages="error?.packageClientPrice"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.packageClientPrice"
                  />
                  <p class="font-weight-bold price-content">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.EURO') }}
                  </p>
                </div>
              </div>
            </v-col>

            <!--TRANSPORT PRODUCT COST-->
            <v-col cols="12">
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="font-weight-bold">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.TRANSPORT') }}
                  </p>
                  <div class="d-flex">
                    <p class="text-field mb-0">
                      {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REF_ERP') }}
                    </p>
                    <p class="text-field font-weight-bold ml-2 mb-0">
                      {{ currentRepairFile.initial_package_transport_ref_code }}
                    </p>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-text-field
                    class="number-text-field"
                    v-model="transportCost"
                    type="number"
                    :error-messages="error?.transportCost"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.transportCost"
                  />
                  <p class="font-weight-bold price-content">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.EURO') }}
                  </p>
                </div>
              </div>
            </v-col>

            <!--MANAGEMENT PRODUCT COST-->
            <v-col cols="12">
              <div class="d-flex justify-space-between align-center">
                <div>
                  <p class="font-weight-bold">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.MANAGEMENT') }}
                  </p>
                  <div class="d-flex">
                    <p class="text-field mb-0">
                      {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SUB_TITLE.REF_ERP') }}
                    </p>
                    <p class="text-field font-weight-bold ml-2 mb-0">
                      {{ currentRepairFile.initial_customer_code }}
                    </p>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <v-text-field
                    class="number-text-field"
                    v-model="additionalRepairCost"
                    type="number"
                    :error-messages="error?.additionalRepairCost"
                    :disabled="!currentRepairFile.product_boomerang_id"
                    :hide-details="!error?.additionalRepairCost"
                  />
                  <p class="font-weight-bold price-content">
                    {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.EURO') }}
                  </p>
                </div>
              </div>
            </v-col>

            <v-col cols="12">
              <p class="d-flex justify-end font-weight-bold total-content">
                {{
                  $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.TOTAL') +
                  getTotalCost() +
                  $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.SELECT.EURO')
                }}
              </p>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <div>
            <p>
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.DELIVERY_DATE_1') }}
            </p>
            <p>
              {{ $t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.DETAIL.DELIVERY_DATE_2') }}
            </p>
          </div>
        </v-col>

        <v-col cols="6" class="pt-0">
          <div class="d-flex align-center">
            <v-menu :close-on-content-click="false">
              <template v-slot:activator="{ props }">
                <img
                  v-bind="props"
                  alt="export"
                  class="date-picker"
                  src="@/assets/icons/repair-file-list/date.svg"
                  @click="showValidDate = true"
                />
                <v-text-field
                  v-bind="props"
                  class="ml-3 date-creation"
                  v-model="realDeliveryDate"
                  :value="formatDate()"
                  readonly
                  hide-details
                  @click="showValidDate = true"
                />
                <v-icon
                  icon="mdi-check-circle-outline"
                  color="#767676"
                  class="update-icon"
                  v-if="showValidDate"
                  @click="showValidDate = false"
                />
              </template>
              <v-locale-provider locale="fr">
                <v-date-picker
                  v-model="realDeliveryDate"
                  hide-header
                  color="#559A1B"
                  locale="fr"
                  :max="today"
                  :min="currentRepairFile.finalization_date"
                  @focusin="showValidDate = true"
                />
              </v-locale-provider>
            </v-menu>
          </div>
        </v-col>

        <v-col cols="12">
          <v-btn
            class="boom-button w-100"
            :disabled="
              !deliveryClientFormValid ||
              loading ||
              !currentRepairFile.product_boomerang_id ||
              this.checkGuaranteeValidity()
            "
            :loading="loading"
            variant="outlined"
            @click="submit"
          >
            {{ $t('COMMON.BUTTON.SAVE') }}
          </v-btn>
        </v-col>
        <v-col cols="12">
          <v-btn
            class="button w-100"
            :loading="loading"
            variant="outlined"
            @click="cancelDeliveryClient()"
          >
            {{ $t('COMMON.BUTTON.CANCEL') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { displayToast, handleResponseError, shouldShowTooltip } from '@/components/utils/globalFunction'
import RepairFileEndStatusConstant from '@/constants/repairFileEndStatusConstant'
import ProductService from '@/services/ProductService'
import ModelSerialNumberService from '@/services/ModelSerialNumberService'
import RepairFileService from '@/services/RepairFileService'
import { auth as $store } from '@/store/AuthModule'
import ResponseConstant from '@/constants/responseConstant'
import RepairFileGuaranteeConstant from '@/constants/repairFileGuaranteeConstant'
import { RepairFile } from '@/models/RepairFile'
import RepairFilePricingTypeConstant from '@/constants/repairFilePricingTypeConstant'
import Validator from '@/components/utils/validator'

export default {
  props: {
    currentRepairFile: RepairFile
  },
  data() {
    const date = new Date()
    date.setDate(date.getDate())
    date.setHours(0, 0, 0, 0)

    return {
      error: null,
      productBoomerangId: null,
      productBoomerangName: null,
      product: null,
      serialNumber: null,
      isGenericSerialNumber: false,
      productEndCost: null,
      serialNumberFormat: null,
      billingNumber: null,
      packageClientPrice: null,
      transportCost: null,
      additionalRepairCost: null,

      deliveryClientFormValid: false,
      loading: false,
      getSerialNumber: false,
      deliveryClient: false,
      showReplacementProduct: false,
      dialogVisible: false,

      selectedEndStatus: null,
      endStatusList: Object.keys(RepairFileEndStatusConstant).map((key) => ({
        technical_id: key,
        name: this.$t(
          `REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PRODUCT_END_STATUS.${RepairFileEndStatusConstant[key]}`
        )
      })),

      selectedProduct: null,
      productList: null,

      realDeliveryDate: date,
      today: new Date(),

      showValidDate: false,

      requiredRules: Validator.requiredRules,
      isNumberRules: Validator.isNumberRules,
      positiveNumberRules: Validator.positiveNumberRules
    }
  },
  watch: {
    async selectedEndStatus() {
      if (!this.selectedEndStatus) {
        this.deliveryClient = false
        return
      }
      this.deliveryClient = true

      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED &&
        !this.estimateOptionPreselected &&
        this.currentRepairFile.initial_pricing_type === RepairFilePricingTypeConstant.PACKAGE &&
        this.currentRepairFile.package_repairer_cost_code !== null &&
        this.currentRepairFile.package_repairer_cost_code !== 'DEVIS'
      ) {
        this.productEndCost = this.currentRepairFile.package_repairer_cost_value
      } else {
        this.productEndCost = this.getProductEndCost()
      }

      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED
      ) {
        await this.handleProductRepairedOrRecycled()
      }
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_STANDARD ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_SUPERIOR
      ) {
        this.handleProductReplaced()
      }
    },
    serialNumber() {
      if (this.serialNumber) {
        this.checkFormatValidity()
      }
    },
    async selectedProduct() {
      if (this.selectedProduct) {
        const product = this.productList.find(
          (product) => product.technicalId === this.selectedProduct
        )
        await this.getProduct(product.boomerangId)
      }
    }
  },
  async created() {
    if (!this.currentRepairFile.product_boomerang_id) {
      displayToast(
        this.$t('REPAIR_FILE_DELIVERY_CLIENT_DRAWER.MESSAGE.PRODUCT_BOOMERANG_ID_NOT_FOUND'),
        'warning'
      )
    } else {
      try {
        const response = await ProductService.getProductListBySubCategory(
          this.currentRepairFile.sub_category_boomerang_id
        )
        this.productList = response.item_list
        this.patchData()
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    }
  },
  methods: {
    shouldShowTooltip,
    formatDate() {
      const date = this.realDeliveryDate
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    async submit() {
      try {
        const year = this.realDeliveryDate.getFullYear()
        const month = (this.realDeliveryDate.getMonth() + 1).toString().padStart(2, '0')
        const day = this.realDeliveryDate.getDate().toString().padStart(2, '0')
        const realDeliveryDateFormated = `${year}-${month}-${day}`

        const data = {
          requestor_collaborator_id: $store.state.currentCollaborator.technical_id,
          repair_file_boomerang_id: this.currentRepairFile.boomerang_id,
          product_end_status: this.selectedEndStatus,
          product_boomerang_id: this.getProductBoomerangId(),
          product_serial_number: this.serialNumber,
          product_end_cost: this.productEndCost,
          billing_number: this.billingNumber,
          package_client_price: this.packageClientPrice,
          transport_cost: this.transportCost,
          additional_repair_cost: this.additionalRepairCost,
          real_delivery_date: realDeliveryDateFormated
        }
        await RepairFileService.deliveredRepairFile(data)
        this.$emit('updateCurrentRepairFile')
        this.$emit('closeDeliveryClient')
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    },
    async handleProductRepairedOrRecycled() {
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED &&
        this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE
      ) {
        this.productEndCost = null
        this.packageClientPrice = null
        this.transportCost = null
        this.additionalRepairCost = null
      } else {
        this.packageClientPrice = this.getPackageClientPrice()
        this.transportCost = this.getTransportCost()
        this.additionalRepairCost = this.getAdditionalRepairCost()
      }
      this.productBoomerangId = this.currentRepairFile.product_boomerang_id
      this.productBoomerangName = this.currentRepairFile.product_name
      this.serialNumber = this.currentRepairFile.product_serial_number
      if (this.currentRepairFile.product_boomerang_id) {
        await this.getProduct(this.currentRepairFile.product_boomerang_id)
      }
      await this.getModelSerialNumber()
      this.checkFormatValidity()
      this.showReplacementProduct = false
    },
    handleProductReplaced() {
      if (
        this.estimateOptionPreselected &&
        this.selectedEndStatus !== this.estimateOptionPreselected.offer_type.toUpperCase()
      ) {
        this.productEndCost = null
        this.packageClientPrice = null
        this.transportCost = null
        this.additionalRepairCost = null
      } else {
        this.packageClientPrice = this.getPackageClientPrice()
        this.transportCost = this.getTransportCost()
        this.additionalRepairCost = this.getAdditionalRepairCost()
      }
      this.serialNumber = ''
      this.showReplacementProduct = true
    },

    async getModelSerialNumber() {
      if (this.product && !this.product.modelSerialNumber?.format) {
        this.modelSerialNumber = await ModelSerialNumberService.getBySubCategory(
          this.currentRepairFile.sub_category_boomerang_id
        )
        if (this.modelSerialNumber && this.modelSerialNumber.format) {
          this.getSerialNumber = true
          this.serialNumberFormat = this.modelSerialNumber.format
        } else {
          this.serialNumberFormat = null
        }
      }
      if (this.product && this.product.modelSerialNumber?.format) {
        this.getSerialNumber = true
        this.serialNumberFormat = this.product.modelSerialNumber.format
      }
    },
    getProductBoomerangId() {
      if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPAIRED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED
      ) {
        return this.currentRepairFile.product_boomerang_id
      } else if (
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_STANDARD ||
        this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_REPLACED_SUPERIOR
      ) {
        const selectedProduct = this.productList.find(
          (product) => product.technicalId === this.selectedProduct
        )
        return selectedProduct.boomerangId
      }
    },
    checkFormatValidity() {
      if (this.serialNumberFormat && this.serialNumber?.length !== Number(this.serialNumberFormat)) {
        this.error = {
          serialNumber: this.$t('FILE_STEP_GUARANTEE.ERROR.SERIAL_NUMBER_NOT_GOOD_FORMAT', {
            value: this.serialNumberFormat
          })
        }
        this.isGenericSerialNumber = false
      } else {
        this.error = null
        this.isGenericSerialNumber = this.checkGenericSerialNumber()
      }
    },
    checkGenericSerialNumber() {
      const regex = /^9{5,}$/
      return regex.test(this.serialNumber)
    },
    formatNumber(value) {
      if (value === null || value === 0) {
        return null
      }
      const parsedValue = parseFloat(value)
      return parsedValue % 1 === 0 ? parsedValue.toFixed(0) : parsedValue.toFixed(2)
    },
    patchData() {
      this.estimateOptionPreselected =
        this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.find(
          (option) => option.is_selected === true
        )
      if (this.estimateOptionPreselected) {
        this.selectedEndStatus = this.estimateOptionPreselected.offer_type.toUpperCase()
        this.productEndCost = this.getProductEndCost()
      } else if (
        !this.estimateOptionPreselected &&
        this.currentRepairFile.initial_pricing_type === RepairFilePricingTypeConstant.PACKAGE
      ) {
        this.selectedEndStatus = RepairFileEndStatusConstant.PRODUCT_REPAIRED
        if (
          this.currentRepairFile.package_repairer_cost_code !== null &&
          this.currentRepairFile.package_repairer_cost_code !== 'DEVIS'
        ) {
          this.productEndCost = this.currentRepairFile.package_repairer_cost_value
        }
      } else {
        this.selectedEndStatus = null
      }

      this.billingNumber = this.currentRepairFile.takeover_billing_number || ''
      this.packageClientPrice = this.getPackageClientPrice()
      this.transportCost = this.getTransportCost()
      this.additionalRepairCost = this.getAdditionalRepairCost()
    },
    async getProduct(productBoomerangId) {
      try {
        this.product = await ProductService.getProduct(productBoomerangId)
        if (this.product?.modelSerialNumber?.format) {
          this.getSerialNumber = true
          this.serialNumberFormat = this.product?.modelSerialNumber?.format
        } else {
          this.serialNumberFormat = null
        }
        this.checkFormatValidity()
      } catch (error) {
        handleResponseError(error)
        if (error.response?.data && error.response.data.key in ResponseConstant) {
          this.setError(error.response.data.key)
        }
      }
    },
    setError(key) {
      switch (key) {
        case 'product_does_not_exist_error_key':
          this.error = { productBoomerangName: this.$t('ERROR.' + ResponseConstant[key]) }
          break
        default:
          this.error = ''
      }
    },
    cancelDeliveryClient() {
      this.$emit('cancelDeliveryClient')
    },
    getPackageClientPrice() {
      if (this.estimateOptionPreselected) {
        return this.estimateOptionPreselected.total_cost
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.package_client_price)
    },
    getTransportCost() {
      if (this.estimateOptionPreselected) {
        return
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.initial_package_transport_cost)
    },
    getAdditionalRepairCost() {
      if (this.estimateOptionPreselected) {
        return
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return this.formatNumber(this.currentRepairFile.initial_package_additional_repair_cost)
    },
    getProductEndCost() {
      if (this.estimateOptionPreselected) {
        return (
          this.estimateOptionPreselected.estimate_cost +
          this.estimateOptionPreselected.transport_cost
        ).toFixed(2)
      }
      if (this.currentRepairFile.status_guarantee !== RepairFileGuaranteeConstant.NOT_GUARANTEE) {
        return 0
      }
      return null
    },
    getTotalCost() {
      return (
        Number(this.packageClientPrice || 0) +
        Number(this.transportCost || 0) +
        Number(this.additionalRepairCost || 0)
      ).toFixed(2)
    },
    checkGuaranteeValidity() {
      if (
        (this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RECYCLED &&
          this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE) ||
        (this.selectedEndStatus === RepairFileEndStatusConstant.PRODUCT_RETURN_DISASSEMBLED &&
          this.currentRepairFile.status_guarantee === RepairFileGuaranteeConstant.NOT_GUARANTEE)
      ) {
        return !this.productEndCost
      }
      return false
    }
  }
}
</script>

<style scoped>
.title {
  color: #000;
  text-align: center;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 15px;
}

.sub-title {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.text-field {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 10px;
}

.text-condition {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.container {
  padding: 20px 20px;
}

.button {
  display: flex;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  text-transform: none !important;
  border-radius: 20px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.boom-button {
  padding: 10px 20px !important;
}

:deep(.v-infinite-scroll__side) {
  padding: unset;
}

.dialog-img {
  height: 100%;
  width: 100%;
}

.dialog-picture {
  display: flex;
  width: auto;
  height: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

.dialog-close-btn {
  position: fixed;
  top: -25px;
  right: -25px;
  height: 50px;
  width: 50px;
  background: white;
  border-radius: 50%;
}

.file-svg {
  margin-bottom: 10px;
  filter: invert(50%) sepia(7%) saturate(13%) hue-rotate(358deg) brightness(87%) contrast(93%);
}

.red-cross-svg {
  height: 11px;
  width: 15px;
  filter: invert(49%) sepia(71%) saturate(2395%) hue-rotate(326deg) brightness(100%) contrast(102%);
}

.price-content {
  margin-left: 10px;
  font-size: 20px;
}

.total-content {
  text-align: right;
  font-size: 20px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.underline {
  height: 1px;
  background: #dadada;
  margin-bottom: 10px;
}

.information-content {
  margin-top: 40px;
}

.margin-top-18 {
  margin-top: 18px;
}

.number-text-field {
  width: 200px;
}

.serial-content {
  color: #727272;
  margin-bottom: 10px;
}

.custom-row-overflow {
  overflow: auto;
  max-height: 90vh;
  margin-right: -21px;
  margin-top: -21px;
}

.generic-serial-number {
  color: var(--warning-color);
  margin-top: 8px;
  margin-left: 20px;
  font-size: 12px;
  line-height: 12px;
  word-break: break-word;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  transition-duration: 0.15s;
}

.clickable {
  cursor: pointer;
}

.date-picker {
  width: 16px;
  filter: invert(48%) sepia(0%) saturate(78%) hue-rotate(135deg) brightness(95%) contrast(83%);
}

.date-creation {
  display: flex;
  justify-items: center;
  color: #767676;
  padding: 4px;
  min-width: 125px;
}

:deep(.v-input__control) {
  min-width: 125px;
}

.date-picker,
.date-creation {
  cursor: pointer;
}
</style>
